import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { TeaListener } from '../tea-listener.ts';
import { useForceUpdate } from './use-force-update.ts';

export function useTeaEditor(fieldId: string) {
  const editorRef = useRef<Editor | undefined>(null);
  const subRef = useRef<TeaListener['initSubscriber'] | undefined>(null);
  const initSubRef = useRef<TeaListener['initSubscriber'] | undefined>(null);
  const forceUpdate = useForceUpdate();

  const updateSubscriber = useMemo(() => {
    const client = new TeaListener(fieldId);

    const sub = client.on('editor-updated', ({ editor }) => {
      editorRef.current = editor;
      forceUpdate();
    });

    if (subRef.current) {
      subRef.current.unsubscribe();
    }

    if (initSubRef.current) {
      initSubRef.current.unsubscribe();
    }

    initSubRef.current = client.initSubscriber;
    subRef.current = sub;

    client.forceEditorStateUpdate();

    return sub;
  }, [forceUpdate, fieldId]);

  const cleanupSubscription = useCallback(() => {
    updateSubscriber.unsubscribe();
  }, [updateSubscriber]);

  useEffect(() => {
    return cleanupSubscription;
  }, [cleanupSubscription]);

  return {
    editor: editorRef.current,
  };
}
